import { useState, type ReactNode } from 'react'
import { kickoffWithDate } from '../../shared/clock'
import type { Side } from '../../shared/espn'
import { cellState, pct, tally, type Cell } from '../../shared/grid'
import { competitionRank, raceSegments, type Segment } from '../../shared/rank'
import { byReadiness, entryState, type EntryState } from '../../shared/roster'
import { weekScores, type Ruleset } from '../../shared/scoring'
import type { State, StateGame } from '../api'
import { Empty, WeekDates } from '../ui'
import { GameSheet } from './GameSheet'

/*
 * This Week, built from reference/design-handoff/README.md.
 *
 * The screen changes character across a weekend, which is the point. Before
 * anything kicks off it is a nagging list; from each kickoff onward it fills
 * in, one game at a time.
 *
 * NOTHING HERE DECIDES WHAT MAY BE SHOWN. The API has already stripped every
 * pick belonging to a game that has not started, so anything missing from this
 * screen is missing because the server refused to send it. The "HIDDEN UNTIL
 * KICKOFF" chip exists to make that refusal visible rather than to enforce it -
 * an absence that is drawn reads as a rule, and an absence that is simply blank
 * reads as a bug.
 */
export function ThisWeek({
  state,
  tz,
  strip,
}: {
  state: State
  tz: string
  /** The pinned season standing, placed under this screen's own header. */
  strip?: ReactNode
}) {
  const [open, setOpen] = useState<string | null>(null)
  const [view, setView] = useState<'list' | 'grid'>('list')
  const [sheet, setSheet] = useState<string | null>(null)

  if (!state.week) return <Empty>No board is up yet.</Empty>

  const games = state.games
  const live = games.filter((g) => g.started && !g.result?.final)
  const decided = games.filter((g) => g.result?.final)

  const pickOf = new Map<string, Side>()
  for (const p of state.picks) pickOf.set(`${p.gameId}|${p.playerId}`, p.side)

  const board = games.map((g) => ({
    started: g.started,
    final: g.result?.final === true,
    winnerSide: (g.result?.winnerSide as Side | null) ?? null,
    homeScore: g.result?.homeScore ?? null,
    awayScore: g.result?.awayScore ?? null,
  }))

  /*
   * The board in the shape the scorer wants it, once, rather than rebuilt
   * inside a reduce per player.
   */
  const scoredBoard = games.map((g) => ({
    game: { favSide: g.favSide, spread: g.spread },
    result: {
      winnerSide: (g.result?.winnerSide as Side | null) ?? null,
      final: g.result?.final === true,
    },
  }))

  const per = state.players.map((pl) => ({
    pl,
    picks: games.map((g) => pickOf.get(`${g.id}|${pl.id}`) ?? null),
    /*
     * From the server's COUNT, never from `picks` above. Before the board
     * locks everyone else's picks arrive as null, so reading whether somebody
     * picked off that array would call eleven people no-shows.
     */
    made: state.counts.find((c) => c.playerId === pl.id)?.picked ?? 0,
  }))

  const totals = weekScores(
    scoredBoard,
    per.map((x) => x.picks),
    per.map((x) => x.made > 0),
    state.rules,
  )

  const rows = per.map((x, i) => ({
    ...x.pl,
    pts: totals[i] ?? 0,
    picks: x.picks,
    made: x.made,
    total: games.length,
    segments: raceSegments(board, x.picks),
    due: games.length - x.made,
  }))

  /*
   * The sheet stands in for the whole screen rather than opening over it. It
   * is a place, not a dialog: it has a back arrow, it is where a column tap
   * leads, and a modal here would have put a scrolling list inside a scrolling
   * overlay on a phone.
   */
  const openGame = sheet === null ? null : (games.find((g) => g.id === sheet) ?? null)
  if (openGame) {
    return (
      <GameSheet
        game={openGame}
        index={games.indexOf(openGame) + 1}
        players={state.players}
        picks={state.picks.filter((p) => p.gameId === openGame.id)}
        meId={state.me.playerId}
        tz={tz}
        onBack={() => setSheet(null)}
      />
    )
  }

  /*
   * THE SCREEN CHANGES CHARACTER, AND SO DOES THE ORDER.
   *
   * Until something is decided this is a nagging list: everybody has nought,
   * so points cannot sort it, and the useful order is who still owes - the
   * unstarted first, the finished last, where they need no attention.
   *
   * From the first final onward it is a scoreboard and sorts by points, which
   * is what it was always doing and what was wrong about it in the meantime.
   */
  const scoring = decided.length > 0
  rows.sort(scoring ? (a, b) => b.pts - a.pts || a.name.localeCompare(b.name) : byReadiness)

  const places = competitionRank(rows.map((r) => r.pts))
  const behind = rows.filter((r) => r.due > 0)

  /*
   * The outline/fill key for how far along a board is. Gold because filling
   * your board IS something you did - it is the same meaning the colour
   * carries everywhere else, at a lower volume.
   */
  const ENTRY: Record<EntryState, string> = {
    empty: 'border border-hairline',
    partial: 'shadow-[inset_0_0_0_1.5px_var(--color-gold-edge)]',
    done: 'bg-gold-wash shadow-[inset_0_0_0_1.5px_var(--color-gold)]',
  }

  return (
    <div className="pb-5">
      <header className="flex items-baseline justify-between px-[18px] pt-5 pb-3.5">
        <span className="text-[30px] font-extrabold tracking-[0.03em] uppercase">
          {state.week.label}
        </span>
        <span className="text-right">
          <span className="slate-num block text-[10.5px] font-medium tracking-[0.1em] text-mute">
            {!state.week.locked && state.week.lockAt !== null ? (
              /*
               * When the week starts, not when it closes - nothing closes
               * board-wide any more. Each game takes picks until it kicks off,
               * and every card carries its own time; what this corner is for
               * is "when does any of this begin".
               */
              <span className="font-bold text-live-deep">
                FIRST GAME {kickoffWithDate(state.week.lockAt, tz)}
              </span>
            ) : (
              <>{decided.length} FINAL</>
            )}
            {state.week.locked && (
              <>
            {live.length > 0 && (
              <>
                {' · '}
                <span className="font-bold text-live-deep">&#9679; {live.length} LIVE</span>
              </>
            )}
              </>
            )}
          </span>
          <WeekDates week={state.week} tz={tz} />
        </span>
      </header>

      {strip}

      {/*
       * Two readings of one week, not two screens.
       *
       * The list answers "how is each person doing"; the grid answers "what did
       * everyone take". Neither is a superset - the list carries running totals
       * and the nag, the grid carries the teams - so this is a toggle rather
       * than a replacement, and it keeps the tab bar at five.
       */}
      <div className="mx-3 mb-3 flex gap-1.5">
        {(['list', 'grid'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            aria-pressed={view === v}
            className={[
              'slate-num slate-tap flex-1 text-[11px] font-bold tracking-[0.16em] uppercase',
              view === v ? 'bg-ink text-card' : 'bg-recessed text-mute',
            ].join(' ')}
            style={{ borderRadius: '3px' }}
          >
            {v}
          </button>
        ))}
      </div>

      {view === 'grid' && (
        <GridView games={games} rows={rows} rules={state.rules} onOpen={setSheet} />
      )}

      {view === 'list' && (
        <>
      {/*
       * The ticker. Only games actually in progress, and only a score - it is
       * a glance, not a screen. Mint borders because a game being on right now
       * is the one thing here that is about time.
       */}
      {live.length > 0 && (
        <div className="mb-3 flex gap-2 overflow-x-auto px-3 pb-1">
          {live.map((g) => (
            <div
              key={g.id}
              className="slate-card min-w-[126px] shrink-0 border-live px-2.5 py-2"
              style={{ borderColor: 'rgb(15 168 122 / 0.45)', borderRadius: '4px' }}
            >
              <TickerLine name={g.away.name} score={g.result?.awayScore ?? null} />
              <TickerLine name={g.home.name} score={g.result?.homeScore ?? null} />
              <span className="slate-num mt-1 block text-[8.5px] font-bold tracking-[0.1em] text-live-deep">
                &#9679; LIVE
              </span>
            </div>
          ))}
        </div>
      )}

      {/* The nag. Only while there is still something to nag about. */}
      {behind.length > 0 && (
        <div
          className="slate-card mx-3 mb-3 px-3 py-2.5"
          style={{ borderColor: 'var(--color-live)' }}
        >
          <span className="slate-num text-[10px] font-bold tracking-[0.1em] text-live-deep uppercase">
            Waiting on {behind.map((r) => r.name).join(', ')}
          </span>
          <span className="slate-num mt-0.5 block text-[9.5px] tracking-[0.06em] text-mute uppercase">
            {behind.reduce((n, r) => n + r.due, 0)} picks due
          </span>
        </div>
      )}

      <ul className="flex flex-col gap-2 px-3">
        {rows.map((r, i) => {
          const isOpen = open === r.id
          /*
           * A leader only once there IS one. With nothing decided every player
           * is tied for first, so this ringed all twelve rows in gold and
           * printed "1" against every name - a signal everybody has is not a
           * signal, and it drowned the one colour that means anything here.
           */
          const leader = scoring && places[i] === 1
          const st = entryState(r)
          return (
            <li
              key={r.id}
              className={[
                'slate-card overflow-hidden',
                leader ? 'shadow-[inset_0_0_0_1.5px_var(--color-gold)]' : scoring ? '' : ENTRY[st],
              ].join(' ')}
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : r.id)}
                aria-expanded={isOpen}
                className="slate-tap flex w-full items-center gap-3 px-3 py-2.5 text-left"
              >
                {/*
                 * The place, or a tick. Before anything is final the number is
                 * "1" for everyone and says nothing; how full your board is, is
                 * the only fact this screen actually has about you yet.
                 */}
                <span
                  className={[
                    'w-6 shrink-0 text-center text-[24px] leading-none font-extrabold',
                    leader ? 'text-gold-text' : 'text-faint',
                  ].join(' ')}
                >
                  {scoring ? (
                    places[i]
                  ) : (
                    <span className={st === 'done' ? 'text-gold-text' : 'text-pale'}>
                      {st === 'done' ? '\u2713' : st === 'partial' ? '\u25D1' : '\u25CB'}
                    </span>
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="truncate text-[20px] leading-none font-bold tracking-[0.02em] uppercase">
                      {r.name}
                    </span>
                    {r.due > 0 && (
                      <span className="slate-num shrink-0 text-[8.5px] font-bold tracking-[0.08em] text-live-deep">
                        {r.due} DUE
                      </span>
                    )}
                  </span>
                  <RaceBar segments={r.segments} />
                </span>

                {/*
                 * The points, over their denominator. A bare number early in a
                 * week says almost nothing while looking like a lot; "2" above
                 * "OF 5" is honest about how little has happened.
                 */}
                <span className="shrink-0 text-right">
                  {/*
                   * Points once there are points; picks made until then. "0 OF
                   * 0" was the honest score of a week nothing has happened in,
                   * and it was also the least useful thing this column could
                   * say - the question before kickoff is how full the board is.
                   *
                   * The count is safe to show: how MANY you have picked is
                   * already public (it is what the nag above is built from);
                   * only WHICH team is withheld until each kickoff.
                   */}
                  <span
                    className={[
                      'slate-num block text-[26px] leading-none font-bold',
                      leader || (!scoring && st === 'done') ? 'text-gold-text' : '',
                      !scoring && st === 'empty' ? 'text-faint' : '',
                    ].join(' ')}
                  >
                    {scoring ? r.pts : r.made}
                  </span>
                  <span className="slate-num block text-[8.5px] tracking-[0.1em] text-mute">
                    OF {scoring ? decided.length : r.total}
                  </span>
                </span>
              </button>

              {isOpen && (
                <ul className="border-t border-hairline bg-ground/60">
                  {games.map((g, gi) => (
                    <GameLine key={g.id} game={g} side={r.picks[gi] ?? null} tz={tz} />
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>
        </>
      )}
    </div>
  )
}

function TickerLine({ name, score }: { name: string; score: number | null }) {
  return (
    <span className="flex items-baseline justify-between gap-2">
      <span className="truncate text-[13px] font-bold tracking-[0.02em] uppercase">{name}</span>
      <span className="slate-num text-[13px] font-bold">{score ?? '—'}</span>
    </span>
  )
}

/*
 * One segment per game, in board order, so two players' bars line up and you
 * can see where the week was won. Gold is a hit; mute is a miss; mint-dashed is
 * still being played; recessed is not yet started - and "not yet started" gives
 * nothing away about what anyone picked.
 */
/*
 * The bar is a live scoreboard, not a progress meter.
 *
 * A game in progress leans toward what it is about to become: your pick ahead
 * is a lighter gold, your pick behind is a light grey. Only a tied game, or one
 * with no score yet, keeps the mint dash - mint means "happening right now",
 * and that is all it is left saying.
 */
const SEGMENT: Record<Segment, string> = {
  won: 'bg-gold',
  ahead: 'bg-gold/45',
  live: 'bg-[repeating-linear-gradient(90deg,var(--color-live)_0_3px,var(--color-live-track)_3px_6px)]',
  behind: 'bg-miss-soft',
  lost: 'bg-miss',
  todo: 'bg-recessed',
}

/*
 * THE BAR CARRIES ITS OWN TRACK.
 *
 * The gaps between segments are just gaps - whatever sits behind the bar shows
 * through them. On a white row that read as ten marks; on a finished row,
 * where the card is now gold wash, cool-grey segments over warm cream left the
 * divisions invisible and the whole week looked like one undifferentiated
 * stripe.
 *
 * A track underneath fixes it once for every row colour there will ever be:
 * the gaps show the track rather than the card, so ten games read as ten
 * games on white, on gold wash, and on anything added later.
 */
function RaceBar({ segments }: { segments: Segment[] }) {
  return (
    <span className="mt-1.5 flex h-[5px] gap-[1.5px] overflow-hidden rounded-[3px] bg-pale">
      {segments.map((s, i) => (
        <span key={i} className={['flex-1', SEGMENT[s]].join(' ')} />
      ))}
    </span>
  )
}

/** One game inside an expanded player. */
function GameLine({ game: g, side, tz }: { game: StateGame; side: Side | null; tz: string }) {
  const done = g.result?.final === true
  const hit = done && side !== null && g.result?.winnerSide === side
  const team = side === 'home' ? g.home.name : side === 'away' ? g.away.name : null

  return (
    <li className="flex items-center justify-between gap-3 border-b border-hairline px-3 py-2 last:border-b-0">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-bold tracking-[0.01em] uppercase">
          {g.away.name} at {g.home.name}
        </span>
        {/*
         * Built as parts and joined, because the first version concatenated
         * separators onto pieces that were sometimes absent and produced
         * "MON 12:10 AM EDT · NBC ·" - a trailing dot that reads as a
         * half-loaded row rather than a game without a television slot.
         */}
        <span className="slate-num mt-0.5 block text-[9.5px] tracking-[0.06em] text-mute">
          {g.started && !done && <span className="font-bold text-live-deep">&#9679; LIVE&nbsp;</span>}
          {[
            done ? 'FINAL' : g.started ? null : kickoffWithDate(g.kickoffAt, tz),
            g.networks[0]?.toUpperCase() ?? null,
            g.started && g.result && g.result.awayScore !== null
              ? `${g.away.name.toUpperCase()} ${g.result.awayScore}–${g.result.homeScore}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>

      {/*
       * The chip. Three states, and the third is the important one: a pick
       * that is not ours to see is DRAWN as withheld rather than left blank,
       * because a blank reads as a bug and a dashed chip reads as a rule.
       */}
      {!g.locked ? (
        <span className="slate-num shrink-0 rounded-[3px] border border-dashed border-pale px-3 py-1.5 text-[10px] tracking-[0.06em] text-faint uppercase">
          Opens at the first kickoff
        </span>
      ) : team === null ? (
        <span className="slate-num shrink-0 px-3 py-1.5 text-[10px] tracking-[0.06em] text-faint uppercase">
          No pick
        </span>
      ) : (
        <span
          className={[
            'slate-num shrink-0 rounded-[3px] px-3 py-1.5 text-[12px] font-semibold tracking-[0.04em] uppercase',
            hit
              ? 'bg-gold text-nightink'
              : done
                ? 'text-faint line-through'
                : 'border border-gold text-gold-text',
          ].join(' ')}
        >
          {team}
          {hit ? ' ✓' : ''}
        </span>
      )}
    </li>
  )
}

/*
 * The grid: one row per player, one column per game, everyone at a glance.
 *
 * It is the race bar with its labels restored - the same picks, in the same
 * board order - so a column here and a segment there are always the same game.
 * That is why the board is sorted by kickoff at the API rather than per screen:
 * columns are only worth reading if their order means something.
 *
 * The cell carries the TEAM and colours the outcome, which is two facts in one
 * square. An outcome-only grid would just be the bars again, and the
 * interesting thing about a wrong pick is which team it was.
 */
const CELL: Record<Cell, string> = {
  // Solid gold, dark ink on top: the only state that claims a point.
  won: 'bg-gold text-nightink border-transparent',
  // Struck, not greyed away. You should still be able to read the team someone
  // got wrong - it is the most interesting cell on the board.
  lost: 'bg-ground text-faint line-through decoration-[1.5px] border-hairline',
  // No winner to call, so no strike. The ruleset may yet have paid this.
  void: 'bg-ground text-mute border-hairline',
  // Committed, unresolved: the ring says you are in, the wash says not yet.
  live: 'bg-gold-wash text-ink border-gold-edge',
  // A pick on a game that has not kicked off. Nothing has happened in it yet.
  open: 'bg-card text-ink border-hairline',
  none: 'bg-card text-faint border-hairline',
}

/*
 * The grid no longer needs to know who you are. It took `meId` for one reason:
 * a blank cell meant "no pick" on your own row and "not telling you" on
 * everyone else's. Picks are public now, so a blank means the same thing on
 * every row.
 */
function GridView({
  games,
  rows,
  rules,
  onOpen,
}: {
  games: readonly StateGame[]
  rows: readonly { id: string; name: string; picks: (Side | null)[] }[]
  rules: Ruleset
  onOpen: (gameId: string) => void
}) {
  /*
   * One shape, read along both axes. A row is one player across every game; a
   * column is every player across one game - the same question, turned ninety
   * degrees, so it is the same function.
   */
  const forTally = games.map((g) => ({
    locked: g.locked,
    started: g.started,
    final: g.result?.final === true,
    winnerSide: (g.result?.winnerSide as Side | null) ?? null,
    favSide: g.favSide,
    spread: g.spread,
  }))
  const rowTallies = rows.map((r) => tally(forTally, r.picks, rules))
  /*
   * The game is REPEATED, once per player. tally walks its games array, so
   * handing it one game and twelve picks reads only the first of them - which
   * is exactly what shipped, a field row saying 1/1 under a game the whole
   * family had picked.
   */
  const colTallies = games.map((_, i) =>
    tally(
      rows.map(() => forTally[i]!),
      rows.map((r) => r.picks[i] ?? null),
      rules,
    ),
  )
  const all = rowTallies.reduce(
    (acc, t) => ({ hits: acc.hits + t.hits, decided: acc.decided + t.decided }),
    { hits: 0, decided: 0 },
  )

  return (
    <div className="px-3">
      {/* The grid scrolls sideways inside its own box; the page never does. */}
      <div className="overflow-x-auto pb-1">
        <div className="min-w-max">
          <div className="flex gap-1">
            {/* Spacer above the name column, matching its sticky ground. */}
            <div className="sticky left-0 z-10 w-[70px] shrink-0 bg-ground" />
            {games.map((g, i) => {
              const running = g.started && !g.result?.final
              return (
                /*
                 * The column header is the way into the game sheet, so it
                 * carries a 44px target even though the design draws the
                 * number tiny. The box grows UPWARD - items-end keeps the
                 * label tight against the grid where it was drawn, rather
                 * than pushing a gap between a column and its own heading.
                 */
                <button
                  key={g.id}
                  type="button"
                  onClick={() => onOpen(g.id)}
                  aria-label={`${g.away.name} at ${g.home.name}`}
                  className={[
                    'slate-num flex min-h-[44px] w-[46px] shrink-0 items-end justify-center pb-1',
                    'text-center text-[8px] font-bold tracking-[0.06em]',
                    running ? 'text-live-deep' : 'text-faint',
                  ].join(' ')}
                >
                  {running && <>&#9679;</>}
                  {String(i + 1).padStart(2, '0')}
                </button>
              )
            })}
            {/* Pinned to the other edge, so the score stays put while the
                middle of the week scrolls past. */}
            <span className="slate-num sticky right-0 z-10 flex w-[46px] shrink-0 items-end justify-center bg-ground pb-1 text-[8px] font-bold tracking-[0.06em] text-faint">
              HIT
            </span>
          </div>

          {rows.map((r, ri) => (
            <div key={r.id} className="flex gap-1 pb-1">
              {/*
               * Sticky, and opaque: the cells scroll underneath it, so the name
               * has to carry the page's own ground or the teams show through.
               */}
              <span className="sticky left-0 z-10 flex w-[70px] shrink-0 items-center bg-ground pr-1.5 text-[12px] font-bold tracking-[0.02em] uppercase">
                <span className="truncate">{r.name}</span>
              </span>

              {games.map((g, i) => {
                const pick = r.picks[i] ?? null
                const cell = cellState(
                  {
                    started: g.started,
                    final: g.result?.final === true,
                    winnerSide: (g.result?.winnerSide as Side | null) ?? null,
                  },
                  pick,
                )
                /*
                 * ESPN's own abbreviation, which is the only one of the three
                 * names that fits. `name` is the fallback for boards published
                 * before it was stored, and deriving one instead would have
                 * gone wrong on the NFL, where the short name is the nickname:
                 * "Lions", never "DET".
                 */
                const side = pick === null ? null : pick === 'home' ? g.home : g.away
                const team = side === null ? null : (side.abbr ?? side.name)
                return (
                  <span
                    key={g.id}
                    className={[
                      'flex h-[34px] w-[46px] shrink-0 items-center justify-center border text-[11px] font-bold tracking-[0.02em] uppercase',
                      CELL[cell],
                    ].join(' ')}
                    style={{ borderRadius: '3px' }}
                  >
                    <span className="truncate px-0.5">
                      {cell === 'none' ? '—' : team}
                    </span>
                  </span>
                )
              })}

              <Rate t={rowTallies[ri]!} className="sticky right-0 z-10 bg-ground" />
            </div>
          ))}

          {/*
           * The field, per game. A low number here is the week's real story -
           * the game that beat almost everybody - and it is only ever computed
           * from finished games, so it can say nothing about a pick that has
           * not been revealed yet.
           */}
          <div className="mt-1 flex gap-1 border-t border-hairline pt-1.5">
            <span className="slate-num sticky left-0 z-10 flex w-[70px] shrink-0 items-center bg-ground pr-1.5 text-[8.5px] font-bold tracking-[0.1em] text-mute uppercase">
              Field
            </span>
            {games.map((g, i) => (
              <Rate key={g.id} t={colTallies[i]!} />
            ))}
            <Rate t={all} className="sticky right-0 z-10 bg-ground" />
          </div>
        </div>
      </div>

      <Legend />
    </div>
  )
}

/*
 * Named states, because a colour that has to be guessed is not information.
 * There is no `hidden` any more: nothing is withheld, so a blank cell means
 * the person did not pick, and "No pick" says that outright.
 */
function Legend() {
  const items: [Cell, string][] = [
    ['won', 'Won'],
    ['lost', 'Lost'],
    ['live', 'Live pick'],
    ['none', 'No pick'],
  ]
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
      {items.map(([c, label]) => (
        <span key={c} className="flex items-center gap-1">
          <span
            className={['h-[11px] w-[11px] border', CELL[c]].join(' ')}
            style={{ borderRadius: '2px' }}
          />
          <span className="slate-num text-[8.5px] font-bold tracking-[0.1em] text-mute uppercase">
            {label}
          </span>
        </span>
      ))}
    </div>
  )
}

/*
 * A hit rate, for a row, a column, or the whole week.
 *
 * The count is on top and the percentage beneath it, deliberately: "3/4" is
 * the fact, and 75% is the reading of it. Four games in, the percentage is a
 * small sample and the fraction says so where a bare number would not.
 *
 * Nothing decided yet prints an em dash rather than 0%, which would say a
 * player had got everything wrong rather than that nothing has finished.
 */
function Rate({ t, className = '' }: { t: { hits: number; decided: number }; className?: string }) {
  const p = pct(t)
  return (
    <span
      className={[
        'slate-num flex h-[34px] w-[46px] shrink-0 flex-col items-center justify-center leading-none',
        className,
      ].join(' ')}
    >
      {p === null ? (
        <span className="text-[11px] text-faint">&mdash;</span>
      ) : (
        <>
          <span className="text-[11px] font-bold text-ink">
            {t.hits}/{t.decided}
          </span>
          <span className="mt-[3px] text-[8.5px] font-medium text-mute">{p}%</span>
        </>
      )}
    </span>
  )
}
